import { Modal } from 'components/containers/Modal';
import { ButtonColorsEnum } from 'constants/button-colors';
import { useOrganizationPlan } from 'hooks/useOrganizationPlan';
import { AppContext } from 'providers/AppProvider';
import React, { useContext } from 'react';

export type PossibleLimits =
  | 'posts'
  | 'profiles'
  | 'timeslot'
  | 'autoRSS'
  | 'basicImport'
  | 'importSource'
  | 'analytics'
  | 'multiple'
  | 'category'
  | 'users';
type NonEmptyArray<PossibleLimits> = [PossibleLimits, ...PossibleLimits[]];

export type PossibleMultipleLimits = 'post' | 'category' | 'timeslot' | 'rss';
export type LimitReachModalState =
  | {
      isOpen: boolean;
      type?: Exclude<PossibleLimits, 'multiple'>;
      remaining?: number;
      urlParameters?: string;
      limits?: never;
    }
  | {
      isOpen: boolean;
      type: 'multiple';
      remaining?: number;
      urlParameters?: string;
      limits: NonEmptyArray<PossibleMultipleLimits>;
    };

type LimitMessageObj = Record<PossibleMultipleLimits, string>;
const multipleLimitsMessage: LimitMessageObj = {
  category: 'Categories',
  post: 'Queued Posts',
  timeslot: 'Timeslots',
  rss: 'Auto-Importers'
};
const getModalContent = (isOwner: boolean, modalState: LimitReachModalState) => {
  const { type, remaining, limits } = modalState;
  if (type === 'multiple' && limits) {
    const limitsExceeded = limits.map(limit => <li key={limit}>{multipleLimitsMessage[limit]}</li>);
    return (
      <>
        <p>Please upgrade your plan, as unarchiving this Category would exceed some of your plan limits:</p>{' '}
        {limitsExceeded}
      </>
    );
  }

  if (type === 'analytics') {
    return isOwner
      ? 'If you would like to view reports for any period, please upgrade to a plan with Advanced Analytics. Otherwise you can only view reports for the last month. '
      : 'Please contact Organization Owner to upgrade to a plan with Advanced Analytics to  view reports for any period. Otherwise you can only view reports for the last month. ';
  }

  if (type === 'category') {
    return isOwner
      ? "You've reached your Categories limit. Please upgrade your plan to be able to add more Categories."
      : "You've reached your Categories limit. Please contact the Organization Owner to be able to add more Categories.";
  }

  if (type === 'timeslot') {
    return isOwner
      ? "You've reached your Timeslots limit. Please upgrade your plan to be able to add more Timeslots."
      : "You've reached your Timeslots limit. Please contact the Organization Owner to upgrade the plan to be able to add more Timeslots.";
  }

  if (type === 'basicImport') {
    return isOwner
      ? 'Please upgrade your plan to Intermediate Importer, to access Bulk Importing of up to 500 posts in one go. '
      : 'Please contact the Organization Owner to upgrade your plan to Intermediate Importer, to access Bulk Importing of up to 500 posts in one go.';
  }

  if (type === 'importSource') {
    return isOwner
      ? "You've reached your Saved Sources limit. Please upgrade your plan to be able to save more Sources."
      : "You've reached your Saved Sources limit. Please contact the Organization Owner to upgrade the plan to be able to save more Sources.";
  }

  if (type === 'autoRSS') {
    return isOwner
      ? "You've reached your Auto-Importers limit. Please upgrade your plan to be able to add more Auto-Importers."
      : "You've reached your Auto-Importers limit. Please contact the Organization Owner to upgrade the plan to be able to add more Auto-Importers.";
  }

  if (type === 'users') {
    return isOwner
      ? "You've reached your User limit. Please upgrade your plan to be able to add more Users."
      : "You've reached your User limit. Please contact the Organization Owner to upgrade the plan to be able to add more Users.";
  }

  if (type === 'profiles') {
    if (remaining && remaining > 0) {
      return isOwner
        ? `You've almost reached your Profile limit, but you can still add ${remaining} more Profiles to stay within it. Please upgrade your plan to increase your limit, or edit your request.`
        : `You've almost reached your Profile limit, but you can still add ${remaining} more Profiles to stay within it. Please contact the Organization Owner to upgrade the plan to increase your limit, or edit your request.`;
    }
    return isOwner
      ? "You've reached your Profile limit. Please upgrade your plan to be able to add more Social Profiles."
      : "You've reached your Profile limit. Please contact the Organization Owner to upgrade the plan to be able to add more Social Profiles.";
  }

  if (type === 'posts') {
    if (remaining && remaining > 0) {
      return isOwner
        ? `You've almost reached your Queued Posts limit, but you can still add ${remaining} more Posts to stay within it. This includes all unpublished posts - whether they are queued, unqueued, scheduled, draft, trashed, or failed. Please upgrade your plan to increase your limit, or edit this Post to have fewer Profiles selected.`
        : `You've almost reached your Queued Posts limit, but you can still add ${remaining} more Posts to stay within it. This includes all unpublished posts - whether they are queued, unqueued, scheduled, draft, trashed, or failed. Please contact the Organization Owner to be able to add more Posts.`;
    }
    return isOwner
      ? "You've reached your Queued Posts limit. This includes all unpublished posts - whether they are queued, unqueued, scheduled, draft, trashed, or failed. Please upgrade your plan to be able to add more Posts."
      : "You've reached your Queued Posts limit. This includes all unpublished posts - whether they are queued, unqueued, scheduled, draft, trashed, or failed. Please contact the Organization Owner to be able to add more Posts.";
  }

  return '';
};

type RedirectUrlObj = Record<PossibleLimits, string>;

export const LimitReachedModal = (): JSX.Element => {
  const { limitReachModalState, setLimitReachedModalState } = useContext(AppContext);
  const { isOwner } = useOrganizationPlan();
  const modalContent = getModalContent(isOwner, limitReachModalState);
  const onClose = () => setLimitReachedModalState({ isOpen: false });
  const baseUrl = '/settings/manage-subscription';
  const redirectUrl: RedirectUrlObj = {
    analytics: `${baseUrl}?src=plan_limit_reached&limit=analytics&context=reports`,
    autoRSS: `${baseUrl}?src=plan_limit_reached&limit=auto_rss`,
    basicImport: `${baseUrl}?src=plan_limit_reached&limit=importer&context=bulk`,
    importSource: `${baseUrl}?src=plan_limit_reached&limit=importer&context=saved_sources`,
    posts: `${baseUrl}?src=plan_limit_reached&limit=posts`,
    profiles: `${baseUrl}?src=plan_limit_reached&limit=accounts`,
    timeslot: `${baseUrl}?src=plan_limit_reached&limit=timeslots`,
    multiple: `${baseUrl}?src=plan_limit_reached&limit=categories`,
    category: `${baseUrl}?src=plan_limit_reached&limit=categories`,
    users: `${baseUrl}?src=plan_limit_reached&limit=users`
  };

  const getSecondaryProps = () => {
    const { type, remaining } = limitReachModalState;
    const baseProps = {
      secondaryButtonAction: () => onClose(),
      secondaryButtonColor: ButtonColorsEnum.secondary
    };
    if (!isOwner) {
      return {};
    }
    if (type === 'profiles' && remaining && remaining > 0) {
      return { ...baseProps, secondaryButtonTitle: 'Edit My Request' };
    }

    if (type === 'posts' && remaining && remaining > 0) {
      return { ...baseProps, secondaryButtonTitle: 'Edit Post' };
    }
  };
  const getPrimaryProps = () => {
    if (!isOwner) {
      return {};
    }
    return {
      primaryButtonTitle: 'View Plans',
      primaryButtonAction: () =>
        limitReachModalState.type ? window.open(redirectUrl[limitReachModalState.type]) : onClose()
    };
  };

  return (
    <Modal
      closeOnEsc
      size="small"
      clickOnOutsideHandler={() => onClose()}
      onClose={() => onClose()}
      showCloseIcon
      title="Upgrade your plan"
      {...getPrimaryProps()}
      {...getSecondaryProps()}>
      <div>
        <span>{modalContent}</span>
      </div>
    </Modal>
  );
};
